import { EngineReportDetails, ScavengeExhaustVm } from '@navstation-portal/reporting/data-model/engine-report';
import { Units } from '@navstation-portal/shared/data-model/ui';
import { TooltipSeverity, getRangeTooltip, isOutOfRange } from '../range-tooltip.utils';

const RANGE_LIMITS = {
  scavengeAirTemperature: { min: 0, max: 55 },
  scavengeAirPressure: { min: 0, max: 4 },
  airInletTemperature: { min: 0, max: 60 },
  barometricPressure: { min: 900, max: 1200 },
  exhaustPressureBeforeTc: { min: 0, max: 4 },
  exhaustPressureAfterTc: { min: 0, max: 4 },
  turbineInletPressure: { min: 0, max: 4 },
  turbineBackPressure: { min: 0 },
} as const;

const SOFT_RANGE_LIMITS = {
  scavengeAirTemperature: { min: 25, max: 60 },
  scavengeAirPressure: { min: 0, max: 3 },
  airInletTemperature: { min: 10, max: 70 },
  turbineInletPressure: { min: 0, max: 3 },
  turbineBackPressure: { min: 10, max: 500 },
} as const;

export function buildScavengeExhaustVm(details: EngineReportDetails | undefined): ScavengeExhaustVm {
  const scavengeAirTemperature = details?.scavengeAirTemperature;
  const scavengeAirTemperatureOutOfRange = isOutOfRange(scavengeAirTemperature, RANGE_LIMITS.scavengeAirTemperature);
  const scavengeAirTemperatureTooltip = getRangeTooltip(
    'Scavenge Air Temperature',
    RANGE_LIMITS.scavengeAirTemperature,
    Units.degreesCelsius
  );
  const scavengeAirTemperatureWarning =
    !scavengeAirTemperatureOutOfRange && isOutOfRange(scavengeAirTemperature, SOFT_RANGE_LIMITS.scavengeAirTemperature);
  const scavengeAirTemperatureWarningTooltip = getRangeTooltip(
    'Scavenge Air Temperature',
    SOFT_RANGE_LIMITS.scavengeAirTemperature,
    Units.degreesCelsius,
    TooltipSeverity.Warning
  );

  const scavengeAirPressure = details?.scavengeAirPressure;
  const scavengeAirPressureOutOfRange = isOutOfRange(scavengeAirPressure, RANGE_LIMITS.scavengeAirPressure);
  const scavengeAirPressureTooltip = getRangeTooltip(
    'Scavenge Air Pressure',
    RANGE_LIMITS.scavengeAirPressure,
    Units.bar
  );
  const scavengeAirPressureWarning =
    !scavengeAirPressureOutOfRange && isOutOfRange(scavengeAirPressure, SOFT_RANGE_LIMITS.scavengeAirPressure);
  const scavengeAirPressureWarningTooltip = getRangeTooltip(
    'Scavenge Air Pressure',
    SOFT_RANGE_LIMITS.scavengeAirPressure,
    Units.bar,
    TooltipSeverity.Warning
  );

  const airInletTemperature = details?.airInletTemperature;
  const airInletTemperatureOutOfRange = isOutOfRange(airInletTemperature, RANGE_LIMITS.airInletTemperature);
  const airInletTemperatureTooltip = getRangeTooltip(
    'Air Inlet Temperature',
    RANGE_LIMITS.airInletTemperature,
    Units.degreesCelsius
  );
  const airInletTemperatureWarning =
    !airInletTemperatureOutOfRange && isOutOfRange(airInletTemperature, SOFT_RANGE_LIMITS.airInletTemperature);
  const airInletTemperatureWarningTooltip = getRangeTooltip(
    'Air Inlet Temperature',
    SOFT_RANGE_LIMITS.airInletTemperature,
    Units.degreesCelsius,
    TooltipSeverity.Warning
  );

  const barometricPressure = details?.barometricPressure;
  const barometricPressureOutOfRange = isOutOfRange(barometricPressure, RANGE_LIMITS.barometricPressure);
  const barometricPressureTooltip = getRangeTooltip(
    'Barometric Pressure',
    RANGE_LIMITS.barometricPressure,
    Units.millibar
  );

  const exhaustPressureBeforeTc = details?.exhaustPressureBeforeTc;
  const exhaustPressureBeforeTcOutOfRange = isOutOfRange(exhaustPressureBeforeTc, RANGE_LIMITS.exhaustPressureBeforeTc);
  const exhaustPressureBeforeTcTooltip = getRangeTooltip(
    'Exhaust Pressure',
    RANGE_LIMITS.exhaustPressureBeforeTc,
    Units.bar
  );

  const exhaustPressureAfterTc = details?.exhaustPressureAfterTc;
  const exhaustPressureAfterTcOutOfRange = isOutOfRange(exhaustPressureAfterTc, RANGE_LIMITS.exhaustPressureAfterTc);
  const exhaustPressureAfterTcTooltip = getRangeTooltip(
    'Exhaust Pressure',
    RANGE_LIMITS.exhaustPressureAfterTc,
    Units.bar
  );

  const turbineInletPressure = details?.turbineInletPressure;
  const turbineInletPressureOutOfRange = isOutOfRange(turbineInletPressure, RANGE_LIMITS.turbineInletPressure);
  const turbineInletPressureTooltip = getRangeTooltip(
    'Turbine Inlet Pressure',
    RANGE_LIMITS.turbineInletPressure,
    Units.bar
  );
  const turbineInletPressureWarning =
    !turbineInletPressureOutOfRange && isOutOfRange(turbineInletPressure, SOFT_RANGE_LIMITS.turbineInletPressure);
  const turbineInletPressureWarningTooltip = getRangeTooltip(
    'Turbine Inlet Pressure',
    SOFT_RANGE_LIMITS.turbineInletPressure,
    Units.bar,
    TooltipSeverity.Warning
  );

  const turbineBackPressure = details?.turbineBackPressure;
  const turbineBackPressureOutOfRange = isOutOfRange(turbineBackPressure, RANGE_LIMITS.turbineBackPressure);
  const turbineBackPressureTooltip = `Turbine back pressure must be ≥ ${RANGE_LIMITS.turbineBackPressure.min} mmH₂O`;
  const turbineBackPressureWarning =
    !turbineBackPressureOutOfRange && isOutOfRange(turbineBackPressure, SOFT_RANGE_LIMITS.turbineBackPressure);
  const turbineBackPressureWarningTooltip = getRangeTooltip(
    'Turbine Back Pressure',
    SOFT_RANGE_LIMITS.turbineBackPressure,
    Units.millimetersOfWater,
    TooltipSeverity.Warning
  );

  return {
    scavengeAirTemperatureOutOfRange,
    scavengeAirTemperatureTooltip,
    scavengeAirTemperatureWarning,
    scavengeAirTemperatureWarningTooltip,
    scavengeAirPressureOutOfRange,
    scavengeAirPressureTooltip,
    scavengeAirPressureWarning,
    scavengeAirPressureWarningTooltip,
    airInletTemperatureOutOfRange,
    airInletTemperatureTooltip,
    airInletTemperatureWarning,
    airInletTemperatureWarningTooltip,
    barometricPressureOutOfRange,
    barometricPressureTooltip,
    exhaustPressureBeforeTcOutOfRange,
    exhaustPressureBeforeTcTooltip,
    exhaustPressureAfterTcOutOfRange,
    exhaustPressureAfterTcTooltip,
    turbineInletPressureOutOfRange,
    turbineInletPressureTooltip,
    turbineInletPressureWarning,
    turbineInletPressureWarningTooltip,
    turbineBackPressureOutOfRange,
    turbineBackPressureTooltip,
    turbineBackPressureWarning,
    turbineBackPressureWarningTooltip,
  };
}
